import React, { FC, useContext } from 'react';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';
import Select from 'react-select';

import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import CurrentUserRankings from 'buyside/components/presentation/RankingsIndexPage/MainContent/currentUserRankings';
import Link from 'buyside/components/presentation/Link/link';
import RankCardLoading from 'buyside/components/presentation/RankingsIndexPage/MainContent/RankCard/rankCardLoading';
import NextRankingPublishDate from 'buyside/components/presentation/RankingsIndexPage/MainContent/nextRankingPublishDate';
import RankCard from 'buyside/components/presentation/RankingsIndexPage/MainContent/RankCard/rankCard';
import RankingMethodologyModal from 'buyside/components/presentation/RankingsIndexPage/MainContent/rankingMethodologyModal';

import { formatCategoryLabel, RANKING_SECTIONS } from 'buyside/util/rankCategoryHelper';
import { newIdeaUrl } from 'buyside/util/urlHelper';

import {
  useRankingsIndexQuery,
  useMainNavigationCurrentUserQuery
} from 'interfaces/graphql-typings';

import styles from '../rankingsIndexPageStyles.scss';

const { rankingCategories } = require('buyside/util/constantsFromRails.json');

const groupedOptions = RANKING_SECTIONS.map(section => ({
  label: section.title,
  options: section.categories.map(category => ({
    value: category,
    label: formatCategoryLabel(category)
  }))
}));

const MainContent: FC<{ isPublic: boolean }> = ({ isPublic }) => {
  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  const { category = 'general' } = useParams();

  const navigate = useNavigate();

  const isValidCategory = rankingCategories.includes(category);

  const { data, loading, fetchMore } = useRankingsIndexQuery({
    variables: { category, pageSize: 10 },
    notifyOnNetworkStatusChange: true,
    skip: !isValidCategory
  });

  const { data: currentUserData } = useMainNavigationCurrentUserQuery({ skip: isPublic });

  const { id: currentUserId, canWriteIdeas, canViewUserDetails, isAllocator, profile } =
    currentUserData?.currentUser || {};

  const { confidence, date } = data?.currentRankingForCategory || {};
  const { endCursor, hasNextPage } = data?.currentRanksForCategory?.pageInfo || {};

  if (!isValidCategory) return <Navigate to="/general" />;

  return (
    <div
      id="mainContent"
      className={classNames(styles.mainContent, {
        [styles.sideNavigationOpen]: sideNavigationOpen
      })}
    >
      <div className={styles.mainContentBody}>
        <h1>Analyst Rankings</h1>

        <h4>
          {formatCategoryLabel(category)}
          {date ? ` - ${moment(date).format('MMMM YYYY')}` : null}
        </h4>
        {!isPublic ? <CurrentUserRankings profileId={profile?.id} /> : null}
        {confidence && canWriteIdeas && !isPublic && !isAllocator ? (
          <p>
            <span>
              <Link to={`${newIdeaUrl()}`}>
                Submit at least {confidence} {formatCategoryLabel(category)} Ideas
              </Link>{' '}
              which carry a positive aggregate return to be included in ranking calculations for
              this section.
            </span>
          </p>
        ) : null}
        <RankingMethodologyModal />
        <Select
          className={styles.mainContentHeaderSelect}
          options={groupedOptions}
          value={groupedOptions.flatMap(g => g.options).find(opt => opt.value === category)}
          onChange={option => {
            if (option) {
              navigate(`/${option.value}`);
              document.getElementById('mainContent')?.scrollTo({ top: 0 });
            }
          }}
        />
        {data?.currentRanksForCategory?.edges?.map(edge =>
          edge?.node ? (
            <RankCard
              key={edge.node.id}
              canViewUserDetails={Boolean(canViewUserDetails)}
              isCurrentUser={edge.node.user?.id === currentUserId}
              isPublic={isPublic}
              rank={edge.node}
            />
          ) : null
        )}
        {loading ? (
          <RankCardLoading />
        ) : hasNextPage ? (
          <Button
            className={styles.loadMoreRankingsButton}
            disabled={isPublic}
            faIcon={isPublic ? faLock : undefined}
            onClick={() => fetchMore({ variables: { afterCursor: endCursor } })}
            text="Load More Rankings"
            type={BUTTON_TYPES.LINK}
          />
        ) : null}
      </div>
      <div className={styles.mainContentFooter}>
        <NextRankingPublishDate />
      </div>
    </div>
  );
};

export default MainContent;
